import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactFlow, { Background, Handle, Position, MarkerType, ReactFlowProvider } from 'reactflow';
import 'reactflow/dist/style.css';
import '../../../styles/homepagecss/ProdLifecycle.css';



// ============= DATA STRUCTURES =============

const phases = [
  { id: 1, name: 'Discovery & Research', shortName: 'Discovery' },
  { id: 2, name: 'Preclinical Development', shortName: 'Preclinical' },
  { id: 3, name: 'Clinical Trials (Phase I-III)', shortName: 'Clinical Trials' },
  { id: 4, name: 'Regulatory Submission', shortName: 'Regulatory' },
  { id: 5, name: 'Market Access & Reimbursement', shortName: 'Market Access' },
  { id: 6, name: 'Commercialization', shortName: 'Commercial' },
  { id: 7, name: 'Post-Market Surveillance', shortName: 'Post-Market' }
];

const threads = [
  { id: 'Data Collection-1', name: 'Clinical Trial Records', dataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV'], phases: [ 3, 4, 5] },
  { id: 'Data Collection-2', name: 'Real-World Evidence', dataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV'], phases: [3, 5, 6, 7] },
  { id: 'Data Collection-3', name: 'Guidelines & Standards', dataTypes: ['PDF', 'DOCX'], phases: [1, 2, 3, 4, 5] },
  { id: 'Data Collection-4', name: 'Regulatory Documents', dataTypes: ['PDF', 'DOCX', 'PPT'], phases: [2, 4] },
  { id: 'Data Collection-5', name: 'HTA & Payer Reports', dataTypes: ['PDF', 'DOCX', 'XLSX'], phases: [5, 6] },
  { id: 'Data Collection-6', name: 'Health Economic Models', dataTypes: ['XLSX'], phases: [5, 7] },
  { id: 'Data Collection-7', name: 'Epidemiology & Disease Burden', dataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV'], phases: [1, 2, 3, 5, 7] },
  { id: 'Data Collection-8', name: 'Quality of Life & PRO Data', dataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV'], phases: [3, 5] },
  { id: 'Data Collection-9', name: 'Cost & Resource Use', dataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV'], phases: [2, 3, 5, 6, 7] },
  { id: 'Data Collection-10', name: 'Competitor Intelligence', dataTypes: ['PDF', 'DOCX', 'XLSX', 'PPT'], phases: [1, 5, 6] },
  { id: 'Data Collection-11', name: 'Safety & Pharmacovigilance', dataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV'], phases: [2, 3, 4, 7] },
  { id: 'Data Collection-12', name: 'Stakeholder Materials', dataTypes: ['PDF', 'DOCX', 'PPT', 'XLSX'], phases: [5, 6, 7] }
];

const features = [
  { id: 'feature-1', name: 'Genie.ai', icon: 'bi-asterisk', supportedDataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV', 'PPT', 'TXT'], description: 'Conversational AI Analytics', phases: [1, 2, 3, 4, 5, 6, 7] },
  { id: 'feature-2', name: 'Textractor', icon: 'bi-bricks', supportedDataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV', 'PPT', 'TXT'], description: 'Data Extraction Engine', phases: [2, 3, 4, 5, 7] },
  { id: 'feature-3', name: 'GenBI Studio', icon: 'bi-kanban', supportedDataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV', 'PPT', 'TXT'], description: 'AI Report Generation', phases: [2, 3, 4, 5, 6, 7] },
  { id: 'feature-5', name: 'SLR AI Toolkit', icon: 'bi-list-task', supportedDataTypes: ['PDF', 'DOCX', 'XLSX', 'CSV', 'PPT', 'TXT'], description: 'Literature Review Automation', phases: [1, 3, 5, 7] }
];

const useCases = [
  // PHASE 1: Discovery (3 -> Keep 3)
  { 
    phase: 1, 
    name: 'Competitive Landscape Exploration', 
    feature: 'Genie.ai',
    description: 'Competitor pipelines and targets',
    tags: ['Competitor Intelligence']
  },
  { 
    phase: 1, 
    name: 'Target Validation Literature Screening', 
    feature: 'SLR AI Toolkit',
    description: 'Systematically screen preclinical research literature',
    tags: ['PRISMA', 'Target Validation']
  },
  { 
    phase: 1, 
    name: 'Disease Biology & Epidemiology Q&A', 
    feature: 'Genie.ai',
    description: 'Query disease pathophysiology and burden data',
    tags: [ 'Epidemiology', 'Prevalence', 'Incidence']
  },

  // PHASE 2: Preclinical (4)
  { 
    phase: 2, 
    name: 'Toxicology Data Extraction for IND', 
    feature: 'Textractor',
    description: 'Extract NOAEL, LOAEL, PK/PD parameters systematically',
    tags: ['NOAEL', 'LOAEL', 'Toxicology', 'PK/PD', 'IND']
  },
  { 
    phase: 2, 
    name: 'IND Application Package Generation', 
    feature: 'GenBI Studio',
    description: 'Auto-generate IND regulatory submission documents',
    tags: ['IND', 'FDA', 'Module 2.4']
  },
  { 
    phase: 2, 
    name: 'Safety Database Construction', 
    feature: 'Textractor',
    description: 'Build structured safety grids from preclinical studies',
    tags: ['Dose', 'Adverse Findings']
  },
  { 
    phase: 2, 
    name: 'Regulatory Guideline Q&A', 
    feature: 'Genie.ai',
    description: 'Interactive queries on FDA/EMA requirements',
    tags: ['FDA', 'EMA', 'Regulatory Strategy']
  },

  // PHASE 3: Clinical Trials (5 -> Reduce to 4, removed "Adverse Event Extraction")
  { 
    phase: 3, 
    name: 'Study Characteristics Extraction', 
    feature: 'Textractor',
    description: 'Extract PICO data and PRO measures from trials',
    tags: ['Study Design', 'PICO', 'PRO']
  },
  { 
    phase: 3, 
    name: 'Clinical Trial Data Analytics', 
    feature: 'Genie.ai',
    description: 'Interactive efficacy, safety, and PRO analysis',
    tags: ['Subgroup Analysis', 'PRO Trends']
  },
  { 
    phase: 3, 
    name: 'Investigator Brochure Generation', 
    feature: 'GenBI Studio',
    description: 'Generate trial summaries and DSMB reports',
    tags: [ 'DSMB', 'Interim Analysis', 'CSR']
  },

  // PHASE 4: Regulatory (5 -> Reduce to 4, removed "Label Positioning Analysis")
  { 
    phase: 4, 
    name: 'CTD Module Generation (2.5, 2.7)', 
    feature: 'GenBI Studio',
    description: 'Auto-generate clinical overview and summaries',
    tags: ['Module 2.5', 'Module 2.7', 'Benefit-Risk']
  },
  { 
    phase: 4, 
    name: 'Integrated Safety Summary Extraction', 
    feature: 'Textractor',
    description: 'Extract AEs, SAEs, labs for regulatory ISS',
    tags: ['ISS', 'Laboratory', 'Vital Signs']
  },
  { 
    phase: 4, 
    name: 'Regulatory Briefing Documents', 
    feature: 'GenBI Studio',
    description: 'Create FDA/EMA advisory committee briefings',
    tags: ['FDA AdCom', 'EMA CHMP']
  },
  { 
    phase: 4, 
    name: 'Regulatory Dossier Compilation', 
    feature: 'GenBI Studio',
    description: 'Compile complete NDA/BLA/MAA submissions',
    tags: ['NDA', 'BLA', 'MAA', 'eCTD', 'Regulatory Filing']
  },

  // PHASE 5: Market Access (5 -> Reduce to 4, removed "Model Parameter & PRO Data Extraction")
  { 
    phase: 5, 
    name: 'HEOR Systematic Literature Reviews', 
    feature: 'SLR AI Toolkit',
    description: 'Conduct SLRs for clinical, utilities, costs, epidemiology',
    tags: ['PRISMA', 'RCTs', 'HTA Evidence']
  },
  { 
    phase: 5, 
    name: 'Global Value Dossier & HTA Submissions', 
    feature: 'GenBI Studio',
    description: 'Generate GVD, AMCP, and HTA dossiers',
    tags: ['HTA', 'GVD', 'AMCP']
  },
  { 
    phase: 5, 
    name: 'Evidence Synthesis & Meta-Analysis Reports', 
    feature: 'GenBI Studio',
    description: 'Generate post-SLR synthesis with forest plots',
    tags: [ 'NMA', 'GRADE', 'PRISMA']
  },

  // PHASE 6: Commercial (5 -> Reduce to 4, removed "Competitive Benchmarking")
  { 
    phase: 6, 
    name: 'Payer Landscape & Access Strategy', 
    feature: 'GenBI Studio',
    description: 'Generate market access and reimbursement reports',
    tags: ['HTA', 'Reimbursement', 'Pricing Strategy']
  },
  { 
    phase: 6, 
    name: 'Stakeholder Engagement Materials', 
    feature: 'GenBI Studio',
    description: 'Create payer presentations and value propositions',
    tags: ['P&T Committees', 'Value Messages', 'KAM']
  },
  { 
    phase: 6, 
    name: 'Medical Affairs Publications', 
    feature: 'GenBI Studio',
    description: 'Generate congress materials and manuscripts',
    tags: [ 'Manuscripts', 'PRO Publications', 'KOL']
  },
  { 
    phase: 6, 
    name: 'RWE & Market Performance Analytics', 
    feature: 'Genie.ai',
    description: 'Interactive analysis of real-world and sales data',
    tags: ['RWE', 'Market Analytics', 'Sales Trends']
  },

  // PHASE 7: Post-Market (5 -> Reduce to 4, removed "Post-Marketing Safety Extraction")
  { 
    phase: 7, 
    name: 'Real-World Evidence SLR', 
    feature: 'SLR AI Toolkit',
    description: 'Screen observational studies and registries',
    tags: [ 'Observational Studies', 'Phase IV']
  },
  { 
    phase: 7, 
    name: 'RWE Evidence Synthesis & PRO Reports', 
    feature: 'GenBI Studio',
    description: 'Generate real-world effectiveness publications',
    tags: ['RWE', 'PRO', 'Cost-Effectiveness']
  },
  { 
    phase: 7, 
    name: 'Post-Approval HTA Re-Assessments', 
    feature: 'GenBI Studio',
    description: 'Prepare updated HTA submissions with RWE',
    tags: ['HTA', 'Price Renegotiation', 'RWE Evidence']
  }
];

// ============= CUSTOM NODES =============

const ThreadNode = ({ data }) => (
  <div className={`thread-node-horizontal ${data.isActive ? 'active' : 'inactive'}`}>
    <Handle type="source" position={Position.Right} />
    <div className="thread-node-content ev-fs-11 ev-bold-reg">
      <span>{data.label}</span>
        {data.dataTypes.map((type, idx) => (
          <span key={idx} className="ms-2 thread-node-data-type">{type}</span>
        ))}
    </div>
  </div>
);

const FabricNode = ({ data }) => (
  <div className="ev-card-7  text-center">
    <Handle type="target" position={Position.Left} />
    <div className="gradient-font6 ev-fs-35">
      <i className="bi bi-boxes"></i>
    </div>
    <div className="ev-small-p-blue ev-bold-med ev-fs-13">{data.label}</div>
    <Handle type="source" position={Position.Right} />
  </div>
);

const FeatureNode = ({ data }) => (
  <div className={`feature-node-vertical ${data.isActive ? 'active' : 'inactive'}`}>
    <Handle type="target" position={Position.Left} />
      <i className={`bi ${data.icon} feature-icon mb-2`}></i>
    <div className="ev-reg-p-dark mb-0">{data.label}</div>
    <div className="ev-xsmall-p-dark ev-reg">{data.description}</div>
  </div>
);

const nodeTypes = {
  threadNode: ThreadNode,
  fabricNode: FabricNode,
  featureNode: FeatureNode
};

// ============= MAIN COMPONENT =============

const HomeProdLifecycle = () => {
  const [activePhase, setActivePhase] = useState(1);
  const [isAutoPlay, setIsAutoPlay] = useState(true);
  const containerRef = useRef(null);
  const [containerDimensions, setContainerDimensions] = useState({ width: 1600, height: 1000 });

  useEffect(() => {
    if (containerRef.current) {
      const { width, height } = containerRef.current.getBoundingClientRect();
      setContainerDimensions({ width, height });
    }
  }, []);

  useEffect(() => {
    if (!isAutoPlay) return;
    const interval = setInterval(() => {
      setActivePhase(prev => (prev >= phases.length ? 1 : prev + 1));
    }, 1200);
    return () => clearInterval(interval);
  }, [isAutoPlay]);

const generateNodes = useCallback(() => {
  const nodes = [];
  const { width, height } = containerDimensions;
  
  // LEFT SIDE: Threads - modify spacing calculation
  const threadStartY = height * 0.02;
  
  threads.forEach((thread, idx) => {
    // Keep the randomXShift but scale it proportionally to width
    const randomXShift = Math.sin(idx * 5) * (width * 0.05);
    
    nodes.push({
      id: thread.id,
      type: 'threadNode',
      position: { 
        x: width * 0.1 + randomXShift,
        // Use proportional spacing based on index/total count
        y: threadStartY + (idx / (threads.length - 1)) * (height * 0.7)
      },
      data: {
        label: thread.name,
        dataTypes: thread.dataTypes,
        isActive: thread.phases.includes(activePhase)
      }
    });
  });

  // CENTER: Fabric - keep same percentage position
  nodes.push({
    id: 'fabric-center',
    type: 'fabricNode',
    position: { 
      x: width * 0.47,
      y: height * 0.33
    },
    data: { label: 'Data Fabric' }
  });

  // RIGHT SIDE: Features - modify spacing calculation
  const featureStartY = height * 0.02;
  
  features.forEach((feature, idx) => {
    nodes.push({
      id: feature.id,
      type: 'featureNode',
      position: { 
        x: width * 0.77,
        // Use proportional spacing based on index/total count
        y: featureStartY + (idx / (features.length - 1)) * (height * 0.6)
      },
      data: {
        label: feature.name,
        icon: feature.icon,
        supportedDataTypes: feature.supportedDataTypes,
        description: feature.description,
        isActive: feature.phases.includes(activePhase)
      }
    });
  });

  return nodes;
}, [activePhase, containerDimensions]);

  const generateEdges = useCallback(() => {
    const edges = [];

    threads.forEach(thread => {
      const isActive = thread.phases.includes(activePhase);
      edges.push({
        id: `${thread.id}-to-fabric`,
        source: thread.id,
        target: 'fabric-center',
        type: 'default',
        animated: isActive,
        style: {
          stroke: isActive ? 'var(--color1)' : 'var(--color8)',
          strokeWidth: isActive ? 1 : 1,
          strokeDasharray: '4, 4'
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isActive ? 'var(--color1)' : 'var(--color8)'
        }
      });
    });

    features.forEach(feature => {
      const isActive = feature.phases.includes(activePhase);
      edges.push({
        id: `fabric-to-${feature.id}`,
        source: 'fabric-center',
        target: feature.id,
        type: 'default',
        animated: isActive,
        style: {
          stroke: isActive ? 'var(--color1)' : 'var(--color8)',
          strokeWidth: isActive ? 1 : 1,
          strokeDasharray: '4, 4'
        },
        markerEnd: {
          type: MarkerType.ArrowClosed,
          color: isActive ? 'var(--color1)' : 'var(--color8)'
        }
      });
    });

    return edges;
  }, [activePhase]);

  const currentUseCases = useCases.filter(uc => uc.phase === activePhase);

  return (
    <div>
      <div className="container-fluid">
      <div className="text-center m-auto col-9 py-3">
       <p className="ev-small-p-blue ev-fs-25 ev-bold-med mb-2 ">Streamline Your Entire Product Lifecycle</p>  
       <p className="ev-reg-p-dark ev-fs-15 ev-reg margin-bottom-3">Designed to transform how you bring life-changing therapies to patients. From the first data point to global launch, Evaior intelligently connects every decision, every team, every insight.</p>  
      </div>
        <div className="row ev-card-4">
          {phases.map(phase => (
            <div key={phase.id} className="col px-1">
              <div
                className={`lifecycle-phase-card ${activePhase === phase.id ? 'active' : ''}`}
                onClick={() => {
                  setActivePhase(phase.id);
                  setIsAutoPlay(false);
                }}
              >
                <div className="phase-number">PHASE {phase.id}</div>
                <div className="phase-name mt-1">{phase.shortName}</div>
              </div>
            </div>
          ))}
        </div>
        
        <div className="text-center mt-3 mb-3">
          <button
            className={`ev-btn-${isAutoPlay ? 'blue' : 'blue'}-outline-round ev-btn-small ev-bold-reg`}
            onClick={() => setIsAutoPlay(!isAutoPlay)}
          >
            <i className={`bi bi-${isAutoPlay ? 'pause' : 'play'}-fill  me-1`}></i>
            {isAutoPlay ? 'Pause Auto Play' : 'Resume Auto Play'}
          </button>
        </div>
      </div>

      <div className="lifecycle-flow-wrapper ev-card-4" ref={containerRef}>
      <div className="container-fluid mt-2">
        <div className="ev-card-8  d-block ev-padding-small">
          {/*<h4 className="ev-med-p-blue ev-fs-13 mb-2">
            &nbsp; {phases.find(p => p.id === activePhase)?.name}
          </h4>*/}
          <div className="row g-2">
            {currentUseCases.map((useCase, idx) => (
              <div key={idx} className="col-lg-3 col-md-4 col-6">
                <div className="ev-card-4 ev-padding-small d-block ev-radius-small lifecycle-usecase-card">
                  <div className="ev-small-p-blue mb-1">{useCase.name}</div>
                  {/*<div className="ev-xsmall-p-dark mb-2">{useCase.description}</div>*/}
                  <div className="mb-2">
                    {useCase.tags.slice(0, 4).map((tag, tagIdx) => (
                      <span key={tagIdx} className="ev-message-card3 ev-btn-small ev-radius-xsmall me-1 mb-1 d-inline-block">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div>
                    <span className="ev-message-card6 ev-btn-xsmall ev-fs-11 ev-radius-large">
                      <i className="bi bi-stars"></i> &nbsp; Go with <span className="ev-bold-med">{useCase.feature}</span>
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
        <ReactFlowProvider>
          <ReactFlow
            nodes={generateNodes()}
            edges={generateEdges()}
            nodeTypes={nodeTypes}
            fitView={false}
            zoomOnScroll={false}
            panOnScroll={false}
            zoomOnPinch={false}
            zoomOnDoubleClick={false}
            nodesDraggable={false}
            nodesConnectable={false}
            elementsSelectable={false}
            preventScrolling={true}
            minZoom={1}
            maxZoom={1}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
            proOptions={{ hideAttribution: true }}
          >
            <Background color="var(--color8)" gap={20} size={1} />
          </ReactFlow>
        </ReactFlowProvider>
      </div>
    </div>
  );
};

export default HomeProdLifecycle;